<template>
    <div
        class="basic-input space-y-1"
        :class="{ 'opacity-50': disabled && !preserveActiveStyle }"
    >
        <BasicFormControlLabel
            v-if="label || $slots.default"
            :for-id="id"
            :required="required"
            @click.stop
        >
            <slot>
                {{ label }}
            </slot>
        </BasicFormControlLabel>
        <div
            ref="target"
            :class="computedWrapperClasses"
            data-testid="select-dropdown-div"
            @click="emits('click:focus', true)"
        >
            <slot name="prependInner">
                <BasicIcon
                    v-if="prependInnerIcon"
                    :name="prependInnerIcon"
                    :class="computedIconClasses"
                    :size="computedPrependInnerIconSize"
                    @click="emits('click:prependInnerIcon')"
                >
                </BasicIcon>
            </slot>
            <slot name="input"> </slot>

            <slot
                v-if="$slots.appendInner || appendInnerIcon"
                name="appendInner"
            >
                <BasicIcon
                    v-if="appendInnerIcon"
                    :name="appendInnerIcon"
                    :class="computedIconClasses"
                    :size="computedAppendInnerIconSize"
                    @click="emits('click:appendInnerIcon')"
                >
                </BasicIcon>
            </slot>
            <BasicIcon
                v-else
                size="14"
                class="fill-transparent"
                :name="validationIcon"
                :class="validationIconClasses"
            ></BasicIcon>
        </div>
        <BasicErrorMessage
            v-if="errorMessage && basicErrorMessageId"
            :id="basicErrorMessageId"
            :error="errorMessage"
        ></BasicErrorMessage>
    </div>
</template>

<script setup lang="ts">
import {
    getSizeDependandClasses,
    iconSizes,
    type Size,
} from '@atoms/BasicFormControl/config';
import { type Size as BasicIconSize } from '@atoms/BasicIcon/sizes';

import BasicFormControlLabel from '@atoms/BasicFormControl/BasicFormControlLabel.vue';
const BasicIcon = defineAsyncComponent(
    () => import('@atoms/BasicIcon/BasicIcon.vue')
);
const BasicErrorMessage = defineAsyncComponent(
    () => import('@atoms/BasicErrorMessage/BasicErrorMessage.vue')
);

export interface BasicFormControlProps {
    prependInnerIcon?: string;
    appendInnerIcon?: string;
    appendInnerIconSize?: BasicIconSize;
    prependInnerIconSize?: BasicIconSize;

    basicErrorMessageId?: string;
    hasError: boolean;

    preserveActiveStyle?: boolean;

    id: string;
    size?: Size;
    label?: string;
    disabled?: boolean;
    isFocused: boolean;
    errorMessage?: string;
    modelValue?: string | number | boolean;
    customBorder?: '!border-input-hover' | '!border-primary';

    bottomBorderRounded?: boolean;
    topBorderRounded?: boolean;

    required?: boolean;
}

const props = withDefaults(defineProps<BasicFormControlProps>(), {
    size: 'm',
    label: '',
    disabled: false,
    errorMessage: '',
    modelValue: undefined,

    prependInnerIcon: '',
    appendInnerIcon: '',
    prependInnerIconSize: undefined,
    appendInnerIconSize: undefined,

    preserveActiveStyle: false,

    basicErrorMessageId: undefined,

    bottomBorderRounded: true,
    topBorderRounded: true,

    required: false,
    customBorder: undefined,
});

export interface BasicFormControlEmits {
    (e: 'click:appendInnerIcon'): void;
    (e: 'click:prependInnerIcon'): void;
    (e: 'click:focus', value: boolean): void;
}

const emits = defineEmits<BasicFormControlEmits>();

const target = ref(null);

const validationIcon = computed(() => (props.hasError ? 'close' : 'check'));

const validationIconClasses = computed(() => ({
    '!fill-primary': props.isFocused && !props.hasError && props.modelValue,
    '!fill-error': props.hasError,
}));

export interface BasicFormControlSlots {
    default?(): unknown;
    input?(): unknown;
    prependInner?(): unknown;
    appendInner?(): unknown;
}

defineSlots<BasicFormControlSlots>();

const computedWrapperClasses = computed(() => {
    const sizeClass = getSizeDependandClasses(props.size);
    return [
        'basic-form-control-wrapper',
        'space-x-2.5',
        'flex',
        'items-center',
        'border',
        'rounded-[5px]',
        'bg-background-1',
        'fade',
        'focus-outline',
        sizeClass,
        {
            '!border-primary':
                props.customBorder === '!border-primary' ||
                (props.isFocused &&
                    !props.hasError &&
                    !props.disabled &&
                    !props.customBorder),
            'hover:border-input-hover': !props.disabled,
            'hover:cursor-not-allowed':
                props.disabled && props.preserveActiveStyle,
            '!border-error': props.hasError,
            'bg-background-2': props.disabled && !props.preserveActiveStyle,
            'rounded-b-none': !props.bottomBorderRounded,
            'rounded-t-none': !props.topBorderRounded,
            '!border-input-hover': props.customBorder === '!border-input-hover',
        },
    ];
});

const computedIconClasses = computed(() => {
    return ['fill-font-3', { 'fill-error': props.hasError }];
});

const computedPrependInnerIconSize = computed(() => {
    return props.prependInnerIconSize || iconSizes[props.size];
});

const computedAppendInnerIconSize = computed(() => {
    return props.appendInnerIconSize || iconSizes[props.size];
});

onClickOutside(target, () => emits('click:focus', false));
</script>
